import * as rxjs from 'rxjs'

import type * as _actions from '../actions'
import * as _effects from '../effects'
import * as _feature from '../feature'
import type * as _reducers from '../reducers'
import * as _selectors from '../selectors'

import { type ActionsConfig, getActionsConfig } from './actions'
import type { PropertiesConfig } from './config'
import * as effects from './effects'
import { type Selectors, createSelectors } from './selectors'
import type { State } from './state'

/**
 * __NOTE:__ this is an immediate `success` instruction that ensures the state
 * is updated without having to update the underlying storage
 */
const ONLY_UPDATE_STATE_INSTRUCTION: _actions.OpManager.RequestGroup.relay.Instruction<
  any,
  any
> = {
  success: true,
}

export abstract class Feature<
  // NOTE: this must NOT have any default! If it does, it won't get inferred,
  // which breaks a lot
  const TPropertiesConfig extends PropertiesConfig.Constraint,
> extends _feature.Feature<
  State<TPropertiesConfig>,
  Selectors<TPropertiesConfig>,
  ActionsConfig<TPropertiesConfig>
> {
  /**
   * When the {@link Feature} is first set up, there is an effect that reads the
   * initial values and writes them into the state for this {@link Feature}. In
   * some cases, you may want to have your effect wait until after that initial
   * set up of the state has happened. In those cases, you can use
   * {@link rxjs.skipUntil} with this property.
   *
   * @example
   * myFeature.selectors.myProp.asObservable.pipe(
   *   rxjs.skipUntil(myFeature.initialized$),
   *   rxjs.tap(() => { console.log('This is only triggered for changes to myProp (not the initial load)') })
   * )
   */
  public readonly initialized$: rxjs.Observable<void>

  /**
   * Used to handle writeProperty requests
   */
  protected abstract onWriteProperty: Parameters<
    typeof this.actions.writeProperty.relay
  >[0]

  /**
   * Used to handle writeAllProperties requests
   */
  protected abstract onWriteAllProperties: Parameters<
    typeof this.actions.writeAllProperties.relay
  >[0]

  constructor(params: Feature.constructor.Params<TPropertiesConfig>) {
    super({
      ...params,
      actions: getActionsConfig(params.properties),
      initialState: { values: {} },
      selectors: () => createSelectors(this, params.properties),
    })
    // If there are initial values, we wait until they have been set. Otherwise,
    // we immediately emit
    this.initialized$ = params.initialValues
      ? this.actions$.pipe(
          rxjs.first(
            action =>
              this.actions.writeAllProperties.success.check(action) &&
              action.origin === effects.WRITE_INITIAL_VALUES_TO_STATE_ORIGIN,
          ),
          rxjs.map(() => undefined),
        )
      : rxjs.of(undefined)

    this.registerEffects(
      // Writing properties
      _effects.create((feature: typeof this) =>
        rxjs.merge(
          // Writes single property
          this.actions.writeProperty.relay(
            (forwardTo, request, ...remainingArgs) =>
              request.onlyUpdateState
                ? rxjs.of(ONLY_UPDATE_STATE_INSTRUCTION)
                : this.onWriteProperty(forwardTo, request, ...remainingArgs),
          ).asObservable,
          // Writes all properties
          this.actions.writeAllProperties.relay(
            (forwardTo, request, ...remainingArgs) =>
              request.onlyUpdateState
                ? rxjs.of(ONLY_UPDATE_STATE_INSTRUCTION)
                : this.onWriteAllProperties(
                    forwardTo,
                    request,
                    ...remainingArgs,
                  ),
          ).asObservable,
        ),
      )(),
      // Writing initial values
      effects.writeInitialValuesToState(params.initialValues),
    )
    for (const prop in params.properties) {
      // For each property, we need to relay their actions to the write action
      this.registerEffects(effects.relayPropertyActionsToWrite(prop))
    }
  }
}
export namespace Feature {
  export type Constraint<
    TPropertiesConfig extends PropertiesConfig.Constraint = any,
  > = Feature<TPropertiesConfig>
  export type Default<
    TPropertiesConfig extends
      PropertiesConfig.Constraint = PropertiesConfig.Default,
  > = Feature<TPropertiesConfig>

  export namespace constructor {
    export interface Params<
      TPropertiesConfig extends PropertiesConfig.Constraint,
    > extends Pick<
        _feature.Feature.constructor.Params<
          State<TPropertiesConfig>,
          Selectors<TPropertiesConfig>,
          ActionsConfig<TPropertiesConfig>
        >,
        'injectables' | 'name' | 'parent'
      > {
      properties: TPropertiesConfig
      initialValues?: InitialValuesSource<TPropertiesConfig>
    }
  }
}

export type InitialValuesSource<
  TPropertiesConfig extends PropertiesConfig.Constraint,
> = rxjs.Observable<State.Values<TPropertiesConfig> | undefined>
